import type { User } from "@auth0/auth0-react";
import type { Analytics } from "../lib/analytics";
import { maskPhoneNumber } from "../utils/phoneValidation";
import { maskEmail } from "../utils/twoFactorHelpers";

export type VerificationMethod = "phone" | "email";

/**
 * 2FA Analytics Service
 * Centralized analytics tracking for all 2FA-related events
 */
export class TwoFactorAnalytics {
	analytics: Analytics;
	user: User | undefined;

	constructor(analytics: Analytics, user: User | undefined) {
		this.analytics = analytics;
		this.user = user;
	}

	getUserEmail(): string {
		return this.user?.email ? maskEmail(this.user.email) : "unknown";
	}

	/**
	 * Track 2FA page view
	 */
	trackPageView(callbackUrl: string) {
		this.analytics.logAuth("2fa_page_viewed", {
			callbackUrl,
			userEmail: this.getUserEmail(),
		});
	}

	/**
	 * Track method selection (Email vs SMS)
	 */
	trackMethodSelection(
		previousMethod: VerificationMethod,
		newMethod: VerificationMethod,
	) {
		this.analytics.logAuth("2fa_method_selected", {
			previousMethod,
			newMethod,
			userEmail: this.getUserEmail(),
		});
	}

	/**
	 * Track phone number input changes
	 */
	trackPhoneInput(hasValue: boolean, isValid: boolean) {
		this.analytics.logAuth("2fa_phone_input_changed", {
			hasValue,
			isValid,
			userEmail: this.getUserEmail(),
		});
	}

	/**
	 * Track when user starts entering code
	 */
	trackCodeInputStarted(
		method: VerificationMethod,
		inputType: "typing" | "paste",
		codeLength?: number,
	) {
		this.analytics.logAuth("2fa_code_input_started", {
			method,
			inputType,
			codeLength,
			userEmail: this.getUserEmail(),
		});
	}

	/**
	 * Track code request attempts
	 */
	trackCodeRequested(method: VerificationMethod) {
		this.analytics.logAuth("2fa_code_requested", {
			method,
			userEmail: this.getUserEmail(),
		});
	}

	/**
	 * Track successful code sending
	 */
	trackCodeSent(method: VerificationMethod, phoneNumber?: string) {
		const eventData: Record<string, string> = {
			method,
			userEmail: this.getUserEmail(),
		};

		if (method === "phone" && phoneNumber) {
			eventData.phoneNumber = maskPhoneNumber(phoneNumber);
		}

		this.analytics.logAuth("2fa_code_sent", eventData);
	}

	/**
	 * Track SMS specifically sent
	 */
	trackSmsSent(phoneNumber: string) {
		this.analytics.logAuth("2fa_sms_sent", {
			method: "phone",
			phoneNumber: maskPhoneNumber(phoneNumber),
			userEmail: this.getUserEmail(),
		});
	}

	/**
	 * Track when all 6 digits have been entered (before verification API call)
	 */
	trackCodeComplete(method: VerificationMethod) {
		this.analytics.logAuth("2fa_code_complete", {
			method,
			userEmail: this.getUserEmail(),
		});
	}

	/**
	 * Track verification attempts
	 */
	trackVerificationStarted(method: VerificationMethod, codeLength: number) {
		this.analytics.logAuth("2fa_verification_started", {
			method,
			codeLength,
			userEmail: this.getUserEmail(),
		});
	}

	/**
	 * Track successful verification
	 */
	trackVerificationSuccess(method: VerificationMethod, callbackUrl: string) {
		this.analytics.logAuth("2fa_verification_succeeded", {
			method,
			userEmail: this.getUserEmail(),
			callbackUrl,
		});
	}

	/**
	 * Track 2FA completion
	 */
	trackCompletion(method: VerificationMethod, callbackUrl: string) {
		this.analytics.logAuth("2fa_completed", {
			method,
			userEmail: this.getUserEmail(),
			callbackUrl,
		});
	}

	/**
	 * Track redirect after successful 2FA verification
	 */
	trackRedirect(
		method: VerificationMethod,
		destination: string,
		reason: "needs_onboarding" | "callback_url" | "fallback",
	) {
		this.analytics.logAuth("2fa_redirect", {
			method,
			destination,
			reason,
			userEmail: this.getUserEmail(),
		});
	}

	/**
	 * Track user canceling 2FA
	 */
	trackCancellation(method: VerificationMethod, callbackUrl: string) {
		this.analytics.logAuth("2fa_canceled", {
			method,
			userEmail: this.getUserEmail(),
			callbackUrl,
		});
	}

	/**
	 * Track request failures
	 */
	trackRequestFailure(
		reason: string,
		method: VerificationMethod,
		error?: string,
		phoneNumber?: string,
	) {
		const eventData: Record<string, string> = {
			reason,
			method,
			userEmail: this.getUserEmail(),
		};

		if (error) {
			eventData.error = error;
		}

		if (phoneNumber) {
			eventData.phoneNumber = maskPhoneNumber(phoneNumber);
		}

		this.analytics.logAuth("2fa_request_failed", eventData);
	}

	/**
	 * Track verification failures
	 */
	trackVerificationFailure(
		reason: "invalid_code" | "network_error",
		method: VerificationMethod,
		errorMessage: string,
	) {
		this.analytics.logAuth("2fa_verification_failed", {
			reason,
			method,
			errorMessage,
			userEmail: this.getUserEmail(),
		});
	}

	/**
	 * Track general errors
	 */
	trackError(reason: string, method: VerificationMethod, userEmail?: string) {
		this.analytics.logAuth("2fa_request_failed", {
			reason,
			method,
			userEmail: userEmail || this.getUserEmail(),
		});
	}
}
